import React, { useRef, useEffect, useState } from 'react';
import Icon from '../AppIcon';
import { buildEmbedUrl } from '../../utils/videoUrlBuilder';

const BunnyPlayer = ({ 
  videoId,
  videoLibraryId,
  config = {}, 
  onTimeUpdate, 
  onComplete, 
  currentTime, 
  duration,
  className = "w-full h-full"
}) => {
  console.log('🚀 BunnyPlayer: Componente iniciado com props:', { 
    videoId, 
    videoLibraryId, 
    config, 
    className 
  });
  
  const iframeRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [error, setError] = useState(null);

  // Construir URL de embed
  const embedUrl = React.useMemo(() => {
    if (!videoId || !videoLibraryId) {
      console.warn('❌ BunnyPlayer: videoId ou videoLibraryId não fornecidos');
      return null;
    }

    try {
      const url = buildEmbedUrl('bunny', {
        video_id: videoId,
        video_library_id: videoLibraryId
      }, {
        autoplay: config.autoplay || false,
        controls: config.controls !== false,
        start: currentTime || 0,
        ...config
      });

      console.log('✅ BunnyPlayer: URL de embed construída:', url);
      return url;
    } catch (error) {
      console.error('❌ BunnyPlayer: Erro ao construir URL:', error);
      setError('Erro ao construir URL do vídeo');
      return null;
    }
  }, [videoId, videoLibraryId, config, currentTime]);

  // Debug: log dos dados do Bunny
  console.log('🔍 BunnyPlayer Debug:', {
    videoId,
    videoLibraryId,
    embedUrl,
    hasVideoId: !!videoId,
    hasLibraryId: !!videoLibraryId,
    config
  });

  useEffect(() => {
    if (embedUrl) {
      setIsLoading(false);
      setError(null);
    }
  }, [embedUrl]);

  const handleMouseMove = () => {
    setShowControls(true);
    clearTimeout(handleMouseMove.timeout);
    handleMouseMove.timeout = setTimeout(() => {
      if (isPlaying) {
        setShowControls(false);
      }
    }, 3000);
  };

  const handleIframeLoad = () => {
    setIsLoading(false);
    console.log('✅ BunnyPlayer: Iframe carregado com sucesso');
  };

  const handleIframeError = () => {
    setIsLoading(false);
    setError('Erro ao carregar vídeo do Bunny CDN');
    console.error('❌ BunnyPlayer: Erro ao carregar iframe');
  };

  // Renderizar erro se não houver dados válidos
  if (!videoId || !videoLibraryId) {
    console.log('❌ BunnyPlayer: Dados insuficientes. videoId:', videoId, 'videoLibraryId:', videoLibraryId);
    return (
      <div className={`${className} bg-black flex items-center justify-center`}>
        <div className="text-white text-center">
          <Icon name="AlertCircle" size={48} className="mx-auto mb-4" />
          <p>Dados do vídeo Bunny inválidos</p>
          <p className="text-sm mt-2">Video ID: {videoId || 'não fornecido'}</p>
          <p className="text-sm">Library ID: {videoLibraryId || 'não fornecido'}</p>
        </div>
      </div>
    );
  }

  // Renderizar erro se houver problema na construção da URL
  if (error) {
    return (
      <div className={`${className} bg-black flex items-center justify-center`}>
        <div className="text-white text-center">
          <Icon name="AlertCircle" size={48} className="mx-auto mb-4" />
          <p>Erro no player Bunny</p>
          <p className="text-sm mt-2">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`${className} relative bg-black`}
      onMouseMove={handleMouseMove}
    >
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black z-10">
          <div className="flex flex-col items-center space-y-4 text-white">
            <div className="animate-spin">
              <Icon name="Loader2" size={40} />
            </div>
            <p>Carregando vídeo do Bunny CDN...</p>
          </div>
        </div>
      )}

      <iframe
        ref={iframeRef}
        src={embedUrl}
        className="w-full h-full"
        frameBorder="0"
        allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
        allowFullScreen
        onLoad={handleIframeLoad}
        onError={handleIframeError}
        title="Bunny CDN Video"
        style={{ border: 'none' }}
      />

      {/* Overlay de controles (simulado) */}
      {!isLoading && showControls && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4 z-20">
          <div className="flex items-center justify-between text-white">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-colors"
              >
                <Icon name={isPlaying ? "Pause" : "Play"} size={20} />
              </button>
              <span className="text-sm">Bunny CDN Player</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Maximize" size={20} className="cursor-pointer hover:text-gray-300" />
            </div>
          </div>
        </div>
      )}

      {/* Indicador de provider */}
      <div className="absolute top-2 right-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded z-20">
        Bunny CDN
      </div>
    </div>
  );
};

export default BunnyPlayer;
